import React, { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Trash2, Eye, EyeOff, ExternalLink, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import BlogTiptapEditor from "@/components/admin/BlogTiptapEditor";

const DEFAULT_CATEGORIES = [
    "Resume Tips", "Interview Tips", "Job Search Advice", "AI for Job Seekers",
    "Career Growth", "For Employers", "General",
];

const EMPTY = {
    title: "", excerpt: "", body: "", cover_image_url: "", category: "Resume Tips",
    tags: "", status: "draft", slug: "", seo_title: "", seo_description: "",
    reading_time: "5 min read", icon_name: "BookOpen",
};

const StatusBadge = ({ status }) => (
    <Badge
        className={status === "published"
            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30"
            : "bg-amber-500/15 text-amber-800 dark:text-amber-400 border border-amber-500/30"}
    >
        {status}
    </Badge>
);

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");          // ""=all | "draft" | "published"
    const [q, setQ] = useState("");
    const [editing, setEditing] = useState(null);      // post or null
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/blog", { params: filter ? { status: filter } : {} });
            setPosts(data.posts || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const filtered = useMemo(() => {
        if (!q) return posts;
        const l = q.toLowerCase();
        return posts.filter((p) =>
            (p.title + p.excerpt + p.category + (p.tags || []).join(" ")).toLowerCase().includes(l),
        );
    }, [posts, q]);

    const openNew = () => {
        setEditing(null);
        setForm(EMPTY);
        setShowForm(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            title: p.title || "", excerpt: p.excerpt || "", body: p.body || "",
            cover_image_url: p.cover_image_url || "",
            category: p.category || "General",
            tags: (p.tags || []).join(", "),
            status: p.status || "draft",
            slug: p.slug || "",
            seo_title: p.seo_title || "",
            seo_description: p.seo_description || "",
            reading_time: p.reading_time || "5 min read",
            icon_name: p.icon_name || "BookOpen",
        });
        setShowForm(true);
    };

    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required.");
            return;
        }
        const payload = {
            ...form,
            tags: form.tags
                ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
                : [],
        };
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/blog/${editing.id}`, payload);
                toast.success("Article updated.");
            } else {
                await api.post("/admin/blog", payload);
                toast.success("Article created.");
            }
            setShowForm(false);
            setEditing(null);
            setForm(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async (p) => {
        if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/blog/${p.id}`);
            toast.success("Deleted.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const togglePublish = async (p) => {
        try {
            if (p.status === "published") {
                await api.post(`/admin/blog/${p.id}/unpublish`);
                toast.success("Unpublished.");
            } else {
                await api.post(`/admin/blog/${p.id}/publish`);
                toast.success("Published.");
            }
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-blog" className="space-y-6">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <p className="overline">Content</p>
                    <h1 className="font-heading font-medium text-3xl text-foreground">Blog</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage every public article on <code>/resources</code> and <code>/blog/:slug</code>.
                    </p>
                </div>
                <Button onClick={openNew} data-testid="blog-new-btn" className="bg-brand-navy text-white">
                    <Plus size={16} className="mr-2" /> New article
                </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search title, excerpt, tag…"
                    className="max-w-xs"
                    data-testid="blog-search-input"
                />
                <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40" data-testid="blog-filter-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All status</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">{filtered.length} article(s)</span>
            </div>

            {loading ? (
                <div className="card-soft p-10 text-center text-muted-foreground flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Loading…
                </div>
            ) : filtered.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <p className="font-heading text-lg">No articles yet.</p>
                    <p className="text-sm text-muted-foreground mt-1">Click “New article” to publish your first one.</p>
                </div>
            ) : (
                <div className="card-soft divide-y divide-border overflow-hidden">
                    {filtered.map((p) => (
                        <div key={p.id} data-testid="blog-row" className="p-4 flex items-center gap-4 flex-wrap">
                            <div className="flex-1 min-w-[260px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <StatusBadge status={p.status} />
                                    <Badge variant="outline" className="text-[11px]">{p.category}</Badge>
                                    <span className="text-xs text-muted-foreground">{p.reading_time}</span>
                                </div>
                                <h3 className="mt-2 font-heading font-semibold text-foreground">{p.title}</h3>
                                <p className="text-sm text-muted-foreground line-clamp-1">{p.excerpt}</p>
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    /blog/<span className="font-mono">{p.slug}</span>
                                    {" · "}updated {(p.updated_at || "").slice(0, 10)}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm" variant="outline"
                                    onClick={() => togglePublish(p)}
                                    data-testid={`blog-toggle-${p.id}`}
                                >
                                    {p.status === "published"
                                        ? (<><EyeOff size={14} className="mr-1" /> Unpublish</>)
                                        : (<><Eye size={14} className="mr-1" /> Publish</>)}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`}>
                                    <Edit2 size={14} className="mr-1" /> Edit
                                </Button>
                                <a
                                    href={`/blog/${p.slug}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center text-xs text-brand-green hover:underline px-2 py-1"
                                    data-testid={`blog-view-${p.id}`}
                                >
                                    <ExternalLink size={14} className="mr-1" /> View
                                </a>
                                <Button size="sm" variant="outline" onClick={() => del(p)} data-testid={`blog-delete-${p.id}`} className="text-red-600 hover:text-red-700">
                                    <Trash2 size={14} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Editor drawer */}
            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/50 flex" data-testid="blog-editor-drawer">
                    <div className="ml-auto w-full max-w-4xl h-full bg-background border-l border-border overflow-y-auto">
                        <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between z-10">
                            <h2 className="font-heading font-semibold text-xl">
                                {editing ? "Edit article" : "New article"}
                            </h2>
                            <button onClick={() => setShowForm(false)} aria-label="Close" data-testid="blog-close-drawer">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground">Title</label>
                                <Input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    data-testid="blog-f-title"
                                    placeholder="e.g. 10 Resume Mistakes Killing Your ATS Score"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground">Excerpt (shown on list cards)</label>
                                <Textarea
                                    value={form.excerpt}
                                    onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                                    rows={2}
                                    data-testid="blog-f-excerpt"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold text-muted-foreground">Category</label>
                                    <Select
                                        value={form.category}
                                        onValueChange={(v) => setForm({ ...form, category: v })}
                                    >
                                        <SelectTrigger data-testid="blog-f-category"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {DEFAULT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-muted-foreground">Status</label>
                                    <Select
                                        value={form.status}
                                        onValueChange={(v) => setForm({ ...form, status: v })}
                                    >
                                        <SelectTrigger data-testid="blog-f-status"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="draft">Draft (hidden)</SelectItem>
                                            <SelectItem value="published">Published</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground">Cover image URL (paste a URL — Unsplash, your CDN, etc.)</label>
                                <Input
                                    value={form.cover_image_url}
                                    onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                                    placeholder="https://images.unsplash.com/..."
                                    data-testid="blog-f-cover"
                                />
                                {form.cover_image_url ? (
                                    <img src={form.cover_image_url} alt="Cover" className="mt-2 rounded-lg max-h-40 object-cover border border-border" />
                                ) : null}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold text-muted-foreground">Tags (comma-separated)</label>
                                    <Input
                                        value={form.tags}
                                        onChange={(e) => setForm({ ...form, tags: e.target.value })}
                                        placeholder="resume, ats, interview"
                                        data-testid="blog-f-tags"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-muted-foreground">Reading time</label>
                                    <Input
                                        value={form.reading_time}
                                        onChange={(e) => setForm({ ...form, reading_time: e.target.value })}
                                        placeholder="6 min read"
                                        data-testid="blog-f-reading"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground">Slug (leave empty to auto-generate from title)</label>
                                <Input
                                    value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                    placeholder="resume-mistakes-killing-ats-score"
                                    data-testid="blog-f-slug"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-muted-foreground">Body</label>
                                <BlogTiptapEditor
                                    value={form.body}
                                    onChange={(html) => setForm((f) => ({ ...f, body: html }))}
                                />
                            </div>

                            <details className="rounded-lg border border-border p-3 bg-muted/40">
                                <summary className="cursor-pointer text-sm font-semibold">SEO overrides (optional)</summary>
                                <div className="mt-3 space-y-3">
                                    <div>
                                        <label className="text-xs font-semibold text-muted-foreground">SEO title</label>
                                        <Input
                                            value={form.seo_title}
                                            onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                                            placeholder="Defaults to article title"
                                            data-testid="blog-f-seo-title"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-muted-foreground">SEO description</label>
                                        <Textarea
                                            value={form.seo_description}
                                            onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                                            placeholder="Defaults to excerpt"
                                            rows={2}
                                            data-testid="blog-f-seo-description"
                                        />
                                    </div>
                                </div>
                            </details>

                            <div className="flex items-center gap-3 pt-4 border-t border-border">
                                <Button
                                    onClick={save}
                                    disabled={saving}
                                    data-testid="blog-save-btn"
                                    className="bg-brand-navy text-white"
                                >
                                    {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                                    {editing ? "Save changes" : "Create article"}
                                </Button>
                                <Button variant="outline" onClick={() => setShowForm(false)} data-testid="blog-cancel-btn">
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
